import { JsonLdScript } from "next-seo";
import type { FaqEntry } from "~/features/sanity/faq/entries";
import { buildAbsoluteSeoUrl } from "~/features/seo/utils";
type FaqQuestionNode = {
  "@type": "Question";
  name: string;
  url?: string;
  acceptedAnswer: {
    "@type": "Answer";
    text: string;
  };
};

/**
 * One `FAQPage` per page, aggregating every FAQ section on it.
 *
 * Deliberately built by hand rather than with next-seo's `FAQJsonLd`: that
 * component runs entries through `processQuestion`, which drops every field
 * except `name`/`acceptedAnswer`, so the per-question `url` (what makes an
 * answer citable at its own anchor) is impossible through it. `JsonLdScript`
 * still gives us next-seo's `safeJsonLdReplacer` and `</script` escaping.
 *
 * Emitted once at the route level, not per section: schema.org expects a single
 * `FAQPage` with a `mainEntity` array, and multiple competing blocks on one page
 * are ignored.
 */
export function FaqPageJsonLd({
  entries,
  href
}: {
  entries: FaqEntry[];
  href?: string | null;
}) {
  // Google treats an empty question or answer as invalid, so drop those rather
  // than emit a block that fails validation as a whole.
  const usable = entries.filter(entry => entry.question && entry.answerText);
  if (!usable.length) {
    return null;
  }
  const absoluteHref = buildAbsoluteSeoUrl(href);
  // `buildAbsoluteSeoUrl` falls back to the raw href when NEXT_PUBLIC_URL is
  // unset; a relative `url` is worse than none, so require an absolute one.
  const canLinkAnchors = Boolean(absoluteHref && /^https?:\/\//.test(absoluteHref));
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: usable.map<FaqQuestionNode>(entry => ({
      "@type": "Question",
      name: entry.question,
      ...(canLinkAnchors ? {
        url: `${absoluteHref}#${entry.slug}`
      } : {}),
      acceptedAnswer: {
        "@type": "Answer",
        text: entry.answerText
      }
    }))
  };
  return <JsonLdScript data={data} scriptKey="faq-jsonld" />;
}